import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiPath, firstListItem } from '@/constants/defaultValues';
import { Delete, Get, Post, Put } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { getLabel } from '@/helpers/Utils';

const initialState = {
  policyDynamicFormList: [],
  policyOvertimeDynamicFormList: [],
  policyListAll: [],
  policyAddUpdateDelete: {},
  policyAssignmentAddUpdateDelete: {},
  policyItem: {},
  policyAssignedItemsList: [],
  notification: { show: false, code: '' },
  loading: false,
  error: '',
};

export const fetchPolicyDynamicForm = createAsyncThunk(
  'policy/fetchPolicyDynamicForm',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/System/GetDynamicFormSpec/${params}/${getCompanyID()}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addPolicy = createAsyncThunk(
  'policy/addPolicy',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/TAPolicy/Add'}`,
        data: params,
      });
      thunkAPI.dispatch(getAllPolicies());
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getAllPolicies = createAsyncThunk(
  'policy/getAllPolicies',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/TAPolicy/GetAll/${getCompanyID()}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getPolicyById = createAsyncThunk(
  'policy/getPolicyById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/TAPolicy/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addPolicyAssignment = createAsyncThunk(
  'policy/addPolicyAssignment',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/PolicyAssignment/Add'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAllAssignedPolicies = createAsyncThunk(
  'policy/fetchAllAssignedPolicies',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/PolicyAssignment/Get'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updatePolicy = createAsyncThunk(
  'policy/updatePolicy',
  async (params, thunkAPI) => {
    try {
      const response = await Put({
        url: `${apiPath}${'api/Lookup/TAPolicy/Update/'}${params.id}`,
        data: params.request,
      });
      thunkAPI.dispatch(getAllPolicies());
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deletePolicy = createAsyncThunk(
  'policy/deletePolicy',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/TAPolicy/Delete/${params}`,
      });
      thunkAPI.dispatch(getAllPolicies());
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const policySlice = createSlice({
  name: 'policy',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.policyItem = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicyDynamicForm.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchPolicyDynamicForm.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.find((x) => x.formCode === 'TAPolicy')) {
          const policyDynamicFormList = action.payload;
          if (policyDynamicFormList && policyDynamicFormList?.length > 0) {
            state.policyDynamicFormList = policyDynamicFormList.map((item) => ({
              ...item,
              label: getLabel(item?.nameAr, item?.nameEn),
            }));
          } else {
            state.policyDynamicFormList = action.payload;
          }
        } else if (
          action.payload?.find((x) => x.formCode === 'TAPolicyOverTime')
        ) {
          const policyOvertimeDynamicFormList = action.payload;
          if (policyOvertimeDynamicFormList && policyOvertimeDynamicFormList?.length > 0) {
            state.policyOvertimeDynamicFormList = policyOvertimeDynamicFormList.map((item) => ({
              ...item,
              label: getLabel(item?.nameAr, item?.nameEn),
            }));
          } else {
            state.policyOvertimeDynamicFormList = action.payload;
          }
        }
        state.error = '';
      })
      .addCase(fetchPolicyDynamicForm.rejected, (state, action) => {
        state.loading = false;
        state.policyDynamicFormList = [];
        state.policyOvertimeDynamicFormList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addPolicy.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(addPolicy.rejected, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getAllPolicies.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllPolicies.fulfilled, (state, action) => {
        state.loading = false;
        const policyListAll = action.payload;
        if (policyListAll && policyListAll?.length > 0) {
          state.policyListAll = policyListAll.map((item) => ({
            ...item,
            label: getLabel(item.nameAr, item.nameEn),
          }));
        } else {
          state.policyListAll = action.payload;
        }
        state.error = '';
      })
      .addCase(getAllPolicies.rejected, (state, action) => {
        state.loading = false;
        state.policyListAll = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getPolicyById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getPolicyById.fulfilled, (state, action) => {
        state.loading = false;
        state.policyItem = action.payload;
        state.error = '';
      })
      .addCase(getPolicyById.rejected, (state, action) => {
        state.loading = false;
        state.policyItem = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addPolicyAssignment.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addPolicyAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = { status: true };
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(addPolicyAssignment.rejected, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchAllAssignedPolicies.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchAllAssignedPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAssignedItemsList = action.payload;
        state.error = '';
      })
      .addCase(fetchAllAssignedPolicies.rejected, (state, action) => {
        state.loading = false;
        state.policyAssignedItemsList = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(updatePolicy.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = action.payload?.data;
        state.notification = { show: true, code: action.payload?.errCode };
        state.error = '';
      })
      .addCase(updatePolicy.rejected, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = {};
        state.error = action.payload?.data ?? 'An error occurred';
      });

    builder
      .addCase(deletePolicy.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deletePolicy.rejected, (state, action) => {
        state.loading = false;
        state.policyAddUpdateDelete = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem } = policySlice.actions;
export default policySlice.reducer;
